<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { onMounted, computed, ref } from "vue";
import { useStore } from "vuex";
import { authHeader } from "./../../../helpers/authservice/auth-header";
import axios from "axios";
import moment from "moment";
import Multiselect from "@suadelabs/vue3-multiselect";
import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
import { useRoute } from "vue-router";
import { announcementCategories } from "@/helpers/constants";

const route = useRoute();
const state = ref({
  title: "",
  description: "",
  category: "organization",
  cameras: [],
  starts_at: "",
  ends_at: "",
});

const isCameraCategory = computed(() => state.value.category === "camera");

const rules = {
  title: {
    required: helpers.withMessage("Announcement title is required", required),
  },
  description: {
    required: helpers.withMessage(
      "Announcement description is required",
      required
    ),
  },
  category: {
    required: helpers.withMessage(
      "Announcement category is required",
      required
    ),
  },
  cameras: {
    requiredForCamera: helpers.withMessage(
      "Select at least one camera for a camera announcement",
      (value) => !isCameraCategory.value || (value && value.length > 0)
    ),
  },
  ends_at: {
    afterStart: helpers.withMessage(
      "End date must be after the start date",
      (value) =>
        !value ||
        !state.value.starts_at ||
        new Date(value) > new Date(state.value.starts_at)
    ),
  },
};

const store = useStore();
const v$ = useVuelidate(rules, state);
const loading = computed(() => store.state.globals.loading);
const cameraOptions = computed(() => store.state.announcement.camera_options);

const title = ref("Announcement");
const items = ref([
  {
    text: "Home",
    href: "/",
  },
  {
    text: "Announcement",
    href: "/announcement/list",
    active: true,
  },
  {
    text: "Edit Announcement",
    active: true,
  },
]);

const submitted = ref(false);
const notification = computed(() => store.state.notification);

const cameraLabel = (camera) =>
  [
    camera.lgu ? `[${camera.lgu.abbreviation || camera.lgu.name}]` : null,
    camera.camera_id,
    camera.location,
  ]
    .filter(Boolean)
    .join(" - ");

// The API serializes timestamps as UTC; datetime-local needs local wall-clock.
const toInputDate = (value) =>
  value ? moment(value).format("YYYY-MM-DDTHH:mm") : "";

const onCategoryChange = () => {
  if (!isCameraCategory.value) state.value.cameras = [];
};

const updateAnnouncement = () => {
  submitted.value = true;
  v$.value.$touch();

  if (v$.value.$invalid) return;

  store.dispatch("announcement/updateAnnouncement", {
    id: state.value.id,
    title: state.value.title,
    description: state.value.description,
    category: state.value.category,
    camera_ids: isCameraCategory.value
      ? state.value.cameras.map((camera) => camera.id)
      : [],
    starts_at: state.value.starts_at || null,
    ends_at: state.value.ends_at || null,
  });
};

const getAnnouncement = async () => {
  store.dispatch("globals/loading", null, { root: true });
  try {
    const { data } = await axios.get(`/announcement/${route.params.id}`, {
      headers: authHeader(),
    });
    const announcement = data.data;
    state.value = {
      ...announcement,
      category: announcement.category || "organization",
      cameras: announcement.cameras || [],
      starts_at: toInputDate(announcement.starts_at),
      ends_at: toInputDate(announcement.ends_at),
    };
    store.dispatch("globals/loaded", null, { root: true });
  } catch (err) {
    console.log(err);
    store.dispatch("globals/loaded", null, { root: true });
  }
};

onMounted(() => {
  store.dispatch("announcement/getCameraOptions");
  getAnnouncement();
});
</script>

<template>
  <Layout>
    <PageHeader back="/announcement/list" :title="title" :items="items" />
    <div class="row">
      <div class="col lg-12">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title mb-0">Edit Announcement</h4>
            </div>
            <div class="card-body">
              <form @submit.prevent="updateAnnouncement" class="mx-2">
                <b-alert
                  v-if="notification && notification.message"
                  show
                  variant="success"
                  class="fade show"
                  role="alert"
                >
                  {{ notification.message }}
                </b-alert>

                <div v-if="notification.messages.length > 0">
                  <b-alert
                    v-for="(message, index) in notification.messages"
                    :key="index"
                    show
                    variant="danger"
                    class="fade show"
                    role="alert"
                  >
                    {{ message }}
                  </b-alert>
                </div>
                <div class="row">
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="title" class="form-label required"
                        >Announcement Title</label
                      >
                      <input
                        autofocus
                        :class="{ 'is-invalid': submitted && v$.title.$error }"
                        v-model="state.title"
                        type="text"
                        class="form-control"
                        placeholder="Enter announcement title..."
                        id="title"
                      />
                      <div
                        v-for="item of v$.title.$errors"
                        :key="item.$uid"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="description" class="form-label required"
                        >Announcement Description</label
                      >
                      <textarea
                        :class="{
                          'is-invalid': submitted && v$.description.$error,
                        }"
                        v-model="state.description"
                        class="form-control"
                        placeholder="Enter announcement description..."
                        id="description"
                      ></textarea>
                      <div
                        v-for="item of v$.description.$errors"
                        :key="item.$uid"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="category" class="form-label required"
                        >Category</label
                      >
                      <select
                        id="category"
                        class="form-select"
                        :class="{ 'is-invalid': submitted && v$.category.$error }"
                        v-model="state.category"
                        @change="onCategoryChange"
                      >
                        <option
                          v-for="option in announcementCategories"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.name }}
                        </option>
                      </select>
                      <div class="form-text">
                        Organization announcements reach everyone. Camera
                        announcements also show up in the Mismatch and Review
                        queues for records from the selected cameras.
                      </div>
                    </div>
                  </div>
                  <div class="col-6" v-if="isCameraCategory">
                    <div class="mb-3">
                      <label for="cameras" class="form-label required"
                        >Cameras</label
                      >
                      <multiselect
                        id="cameras"
                        :class="{ 'is-invalid': submitted && v$.cameras.$error }"
                        :multiple="true"
                        v-model="state.cameras"
                        :close-on-select="false"
                        :options="cameraOptions"
                        :custom-label="cameraLabel"
                        placeholder="Select camera(s)"
                        track-by="id"
                      />
                      <div
                        v-for="item of v$.cameras.$errors"
                        :key="item.$uid"
                        class="text-danger small mt-1"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="starts_at" class="form-label"
                        >Start Date &amp; Time</label
                      >
                      <input
                        v-model="state.starts_at"
                        type="datetime-local"
                        class="form-control"
                        id="starts_at"
                      />
                      <div class="form-text">
                        Leave blank to publish immediately.
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="mb-3">
                      <label for="ends_at" class="form-label"
                        >End Date &amp; Time</label
                      >
                      <input
                        :class="{ 'is-invalid': submitted && v$.ends_at.$error }"
                        v-model="state.ends_at"
                        type="datetime-local"
                        class="form-control"
                        id="ends_at"
                      />
                      <div class="form-text">
                        The announcement is archived automatically once this
                        passes. Leave blank to keep it running until archived
                        manually.
                      </div>
                      <div
                        v-for="item of v$.ends_at.$errors"
                        :key="item.$uid"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-bottom mb-4"></div>

                <div class="row">
                  <div class="col-lg-12">
                    <div class="text-end">
                      <router-link
                        to="/announcement/list"
                        type="submit"
                        class="btn btn-danger mr-3"
                      >
                        <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                        Cancel
                      </router-link>
                      <button type="submit" class="btn btn-success">
                        <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                        Update Announcement
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </Layout>
</template>
